// The assistant's rough cut (#1001, spec docs/specs/editor/assistant-rough-cut.md): the spans of
// the video's time where the speech pauses, and the filler words, as live's video-editor-utils
// finds them (its silence trim and filler cut, read for behaviour). Speech is looked for only
// inside the clips that spoke (decision 6). In each stretch of speech, its lead-in and tail are
// cut, and so is each gap of at least `minSilenceMs` from the speech so far to the next word; each
// is cut `paddingMs` short of its words. Words of two tracks may overlap or nest, so the speech so
// far ends at its latest end, and a stretch reads every word said in it or within `paddingMs` of
// it, one begun before it too, so no pause comes nearer a word than that.
// A filler is cut on its own time (decision 2), inside its stretch, but where another word is
// said over it. Pure.
import { spokenClips } from "./captions/transcribe";
import type { TimedWord } from "./captions/words";
import type { Span, Timeline } from "./timeline";

export interface RoughCutOptions {
  /** Also cut the filler words (`FILLERS`). */
  fillers: boolean;
  /** The shortest gap between two words that is a pause. */
  minSilenceMs: number;
  /** How far short of each word a pause is cut. */
  paddingMs: number;
}

/** Live's silence trim: a pause of half a second, cut 200 ms short of each word. */
export const LIVE_ROUGH_CUT: RoughCutOptions = { fillers: true, minSilenceMs: 500, paddingMs: 200 };

/** The filler words: the unambiguous ones ("like" and "you know" stay, decision 2). */
export const FILLERS: ReadonlySet<string> = new Set(["um", "umm", "uh", "uhh", "uhm", "er", "erm", "ah", "ahh", "hmm", "mm", "mhm"]);

/** A word as the filler list spells it: lower case, its letters and digits alone. */
function plain(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
}

function isFiller(word: TimedWord): boolean {
  return FILLERS.has(plain(word.text));
}

/** A filler's time but where one of `words` is said over it (two tracks speaking at once). */
function unsaid(filler: TimedWord, words: readonly TimedWord[]): Span[] {
  let parts: Span[] = [{ startMs: filler.startMs, endMs: filler.endMs }];
  for (const word of words) {
    if (word.endMs <= filler.startMs || word.startMs >= filler.endMs) continue;
    parts = parts
      .flatMap((part) => [
        { startMs: part.startMs, endMs: Math.min(part.endMs, word.startMs) },
        { startMs: Math.max(part.startMs, word.endMs), endMs: part.endMs },
      ])
      .filter((part) => part.endMs > part.startMs);
  }
  return parts;
}

/** Spans earliest first, those that touch or overlap merged. */
function merged(spans: readonly Span[]): Span[] {
  const out: Span[] = [];
  for (const span of [...spans].sort((one, other) => one.startMs - other.startMs)) {
    const last = out.at(-1);
    if (last !== undefined && span.startMs <= last.endMs) last.endMs = Math.max(last.endMs, span.endMs);
    else out.push({ ...span });
  }
  return out;
}

/**
 * The stretches of speech (decision 6): each word's own time, where every video and sound playing
 * at its start plays too, merged. That time lies inside whichever clip spoke the word, which no
 * word names, so a clip with no words keeps its time past the speech: one after a talking clip,
 * over its last word, or a sound under it.
 */
function stretches(timeline: Timeline, words: readonly TimedWord[]): Span[] {
  const spoken = spokenClips(timeline);
  const own: Span[] = [];
  for (const word of words) {
    const playing = spoken.filter((clip) => clip.startMs <= word.startMs && word.startMs < clip.endMs);
    // A word where no video or sound plays (under a still) is not looked for.
    if (playing.length === 0) continue;
    own.push({
      startMs: Math.max(...playing.map((clip) => clip.startMs)),
      endMs: Math.min(...playing.map((clip) => clip.endMs)),
    });
  }
  return merged(own);
}

/** The spans to cut out of the timeline: its pauses and, with `fillers`, its filler words. */
export function roughCutSpans(timeline: Timeline, words: readonly TimedWord[], options: RoughCutOptions): Span[] {
  const sorted = [...words].sort((one, other) => one.startMs - other.startMs);
  const regions = stretches(timeline, sorted);
  const pad = Math.max(0, options.paddingMs);
  const spans: Span[] = [];
  const cut = (startMs: number, endMs: number) => {
    if (endMs > startMs) spans.push({ startMs, endMs });
  };
  for (const region of regions) {
    // Every word said in the stretch or within `pad` of it, one begun before it included: another
    // track's overlay can part a word's own time from the stretch where it goes on.
    const said = sorted.filter((word) => word.startMs - pad < region.endMs && word.endMs + pad > region.startMs);
    const first = said[0];
    // Never so: a stretch holds the word it was made from.
    if (first === undefined) continue;
    cut(region.startMs, first.startMs - pad);
    // Where the speech so far ends: its latest end, since a word may lie inside another.
    let reach = first.endMs;
    for (const word of said.slice(1)) {
      if (word.startMs - reach >= options.minSilenceMs) cut(reach + pad, word.startMs - pad);
      reach = Math.max(reach, word.endMs);
    }
    cut(reach + pad, region.endMs);
    if (options.fillers) {
      const words = said.filter((word) => !isFiller(word));
      for (const filler of said.filter(isFiller)) {
        // Inside the stretch alone, where every word said over it is known.
        for (const part of unsaid(filler, words)) cut(Math.max(part.startMs, region.startMs), Math.min(part.endMs, region.endMs));
      }
    }
  }
  return merged(spans);
}

/** Why a plan's rough cut is not made, in the panel's words. */
export const ROUGH_CUT = {
  noCaptions: "There are no captions to cut by: ask for Auto Captions first.",
  once: "A plan makes one rough cut.",
  noWords: "The captions hold no words to cut by.",
  nothing: (minSilenceMs: number) => `The speech has no pause of ${minSilenceMs / 1000} s or more and no filler word to cut.`,
} as const;
